// Libs
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Yup from "yup";
// Types
import { LoginForm } from "../../Libs/Types/Login";
import {
  FailureResponseWrapper,
  LoginAPI_Response,
  SuccessResponseWrapper,
} from "../../Libs/Types/API-Responses";
// Components
import Logo from "../Logo";
// UI-Components
import { Label } from "../../UI-Components/Label";
import { Input } from "../../UI-Components/Input";
import { Button } from "../../UI-Components/Button";
import { useNavigate } from "react-router-dom";
import Spinner from "../../UI-Components/Spinner";
// Utils
import { setLocalStorageToken } from "../../Libs/Utils/utils";
// Endpoints
import { apiLogin } from "../../Endpoints/Auth";

export default function Login() {
  const navigate = useNavigate();
  const initialValues: LoginForm = {
    email: "",
    password: "",
  };

  // Login Validaiton Schema
  const LoginFormValidationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(4, "Password must be at least 4 characters")
      .required("Password is required"),
  });

  // API Response Handling
  const { mutateAsync: userLogin, isPending: isUserLoginPending } = useMutation(
    {
      mutationFn: apiLogin,
      retry: 0,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<LoginAPI_Response> = data.data;
        localStorage.setItem("token", JSON.stringify(respData.data));
        setLocalStorageToken(JSON.stringify(respData.data));
        navigate("/dashboard", { replace: true });
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    }
  );

  // Handle Login Submit
  const handleLoginFormSubmit = async (values: LoginForm) => {
    await userLogin(values);
  };

  return (
    <>
      <div className="grid grid-rows-10 h-full">
        <div className="row-span-2 place-items-center place-content-center">
          <Logo />
        </div>
        <div className="row-span-1 place-items-center"></div>
        <div className="row-span-1 place-items-center">
          <span className="block text-4xl text-primary font-bold">LOG IN</span>
        </div>
        <div className="row-span-5 place-items-center">
          <Formik
            initialValues={initialValues}
            validationSchema={LoginFormValidationSchema}
            onSubmit={handleLoginFormSubmit}
          >
            <Form className="w-full">
              <div className="mb-2">
                <Label
                  className="block text-primary text-xl font-semibold"
                  htmlFor="email"
                >
                  Email
                </Label>
                <Field
                  as={Input}
                  className="w-full py-2 border-primary text-title outline-none rounded transition"
                  type="email"
                  id="email"
                  name="email"
                  // required
                  pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                  placeholder="Enter Email"
                />
                <ErrorMessage
                  name="email"
                  component="p"
                  className="text-red-500 text-sm"
                />
              </div>
              <div className="mb-2">
                <Label
                  className="block text-primary text-xl font-semibold"
                  htmlFor="password"
                >
                  Password
                </Label>

                <Field
                  as={Input}
                  className="w-full py-2 border-primary text-title outline-none rounded transition"
                  type="password"
                  id="password"
                  name="password"
                  minLength={4}
                  // required
                  placeholder="Enter Password"
                />
                <ErrorMessage
                  name="password"
                  component="p"
                  className="text-red-500 text-sm"
                />
                <span className="block mt-1 text-primary text-xl font-medium place-self-end">
                  Forgot Password?
                </span>
              </div>
              <div className="">
                <Button className="w-full" type="submit">
                  {isUserLoginPending ? (
                    <Spinner className="w-6 h-6" />
                  ) : (
                    "Log In"
                  )}
                </Button>
              </div>
            </Form>
          </Formik>
        </div>
      </div>
    </>
  );
}
